import { FORM_TYPE_CREATE_SPARK } from 'js/constants/formTypes';
import { useCallback } from 'react';
import usePaging from 'new/state/paging/repository/usePaging';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import flowTypes from 'new/components/flows/types/flowTypes';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';

export default function useSpark() {
  const {
    createSource: createSourceFromForm,
    updateSource: updateSourceFromForm,
    createSink: createSinkFromForm,
    updateSink: updateSinkFromForm,
  } = useFormsAdapter(FORM_TYPE_CREATE_SPARK);
  const { getSourceConfigFields, getMetaInfoById, getAuthFields } = usePaging();

  const {
    onSelectConnection,
    end,
    start,
    resetFormData,
    effectiveConnectionType,
    data,
    updateFormData,
  } = useFlowsSetup(FORM_TYPE_CREATE_SPARK, flowTypes.spark);

  const { selectedConnection, credentialId } = data || {};

  const { connectionCategory } = getMetaInfoById(selectedConnection) || {};

  const createSource = useCallback(
    async (sourceInfo) => {
      const response: any = await createSourceFromForm({
        credentialId,
        sourceType: selectedConnection,
        connectionCategory,
        shouldActivateSource: true,
        sourceInfo,
      });
      return response?.payload?.formData?.source;
    },
    [createSourceFromForm, credentialId, selectedConnection, connectionCategory],
  );

  const createSink = useCallback(
    async (sinkInfo) => {
      const response: any = await createSinkFromForm({
        formType: FORM_TYPE_CREATE_SPARK,
        credentialId,
        sinkType: selectedConnection,
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [createSinkFromForm, credentialId, selectedConnection],
  );

  const updateSource = useCallback(
    async (sourceInfo, runWithIngestion?: boolean) => {
      const response: any = await updateSourceFromForm({
        sourceInfo,
        credentialId,
        sourceType: selectedConnection,
        connectionCategory,
        runWithIngestion: runWithIngestion ?? data.runWithIngestion ?? false,
      });
      return response?.payload?.formData?.source;
    },
    [
      updateSourceFromForm,
      credentialId,
      selectedConnection,
      connectionCategory,
      data.runWithIngestion,
    ],
  );

  const updateSink = useCallback(
    async (sinkInfo) => {
      const response: any = await updateSinkFromForm({
        sinkInfo,
      });
      return response?.payload?.formData?.sink;
    },
    [updateSinkFromForm],
  );

  return {
    ...data,
    startForm: start,
    endForm: end,
    getMetaInfoById,
    getSourceConfigFields,
    connectionCategory,
    getAuthFields,
    createSource,
    createSink,
    updateSource,
    updateSink,
    onSelectConnection: onSelectConnection(flowTypes.spark),
    effectiveConnectionType,
    updateFormData,
    resetFormData,
  };
}
